import { getApiBase } from "./api-env";
import { getAccessToken, refreshAccessToken } from "./auth";

async function request<T>(path: string, options?: RequestInit): Promise<T> {
    const token = getAccessToken();
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(options?.headers as Record<string, string>)
    };
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    let res = await fetch(`${getApiBase()}${path}`, {
        ...options,
        headers
    });

    if (res.status === 401 && token) {
        const newToken = await refreshAccessToken();
        if (newToken) {
            headers["Authorization"] = `Bearer ${newToken}`;
            res = await fetch(`${getApiBase()}${path}`, { ...options, headers });
        } else {
            // Refresh returned null — a transient failure (network/timeout/5xx/
            // 429). performRefresh now owns the logout-on-401 decision, so we
            // must NOT log the nurse out here. Surface a retryable error.
            throw new Error("Couldn't refresh your session. Please try again.");
        }
    }

    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg =
            typeof body.error === "string"
                ? body.error
                : body.error?.message || `Request failed: ${res.status}`;
        throw new Error(msg);
    }

    return res.json();
}

// --- Types ---

export interface ObItemOption {
    id: string;
    itemId: string;
    label: string;
    value: string;
    orderIndex: number;
}

export interface ObBranchingRule {
    id: string;
    parentItemId: string;
    childItemId: string;
    parentValue: string;
    triggerType: "user_answer" | "ai_extraction";
}

export interface ObAiConfig {
    id: string;
    itemId: string;
    verificationMethod: "llm" | "aws_face" | "crawler" | "email_verification" | "none";
    consensusThreshold: number;
}

export interface ObItemNode {
    id: string;
    subsectionId: string;
    name: string;
    shortLabel: string | null;
    description: string | null;
    itemType: string;
    mandatory: boolean;
    activationGate: "must_before" | "can_after" | null;
    hiddenOnMobile: boolean;
    orderIndex: number;
    options: ObItemOption[];
    branchingRules: ObBranchingRule[];
    aiConfig: ObAiConfig | null;
}

export interface ObSubmission {
    id: string;
    status: string;
    answerValue: string | null;
    answerJson: unknown;
    rejectionReason: string | null;
    submittedAt: string | null;
    reviewedAt: string | null;
    uploads: ObUpload[];
}

export interface ObUpload {
    id: string;
    submissionId: string;
    s3Url: string;
    viewUrl: string | null;
    fileType: string;
    originalFilename: string;
    isCurrent: boolean;
    createdAt: string;
}

export interface ObItemWithSubmission extends ObItemNode {
    submission: ObSubmission | null;
}

export interface ObSubsectionNode {
    id: string;
    sectionId: string;
    name: string;
    description: string | null;
    subsectionType: "form" | "photo_upload_section";
    orderIndex: number;
    items: ObItemWithSubmission[];
}

export interface ObSectionNode {
    id: string;
    name: string;
    description: string | null;
    icon: string | null;
    color: string | null;
    activationGate: "must_before" | "can_after";
    /** Locked (not enterable) until every other required section is complete. */
    unlockAfterRequired?: boolean;
    orderIndex: number;
    subsections: ObSubsectionNode[];
}

export interface OnboardingProgress {
    totalApproved: number;
    totalRequired: number;
    mustBeforeComplete: boolean;
    /** Every required must_before item is handed in (submitted or beyond) —
     *  the application is done from the nurse's side and under review. */
    allSubmitted: boolean;
    sections: { sectionId: string; approved: number; total: number }[];
}

export interface ClassificationRejection {
    id: number;
    uploadId: number;
    rejectionReason: string;
    createdAt: string;
    /** Which subsection the rejected upload belonged to. Null when the upload
     *  or its submission has since been removed. A screen must only render
     *  rejections from its own subsection: these used to arrive unscoped, so a
     *  nurse on Tests, Labs & Vaccines was shown identity-step rejections
     *  ("this is a driving license…") with nothing to connect them to. */
    subsectionId: number | null;
}

export interface OnboardingState {
    onboarding: {
        id: string;
        rnId: string;
        startedAt: string;
        /** Set when every required item was approved — awaiting activation. */
        completedAt: string | null;
        activatedAt: string | null;
    };
    config: ObSectionNode[];
    progress: OnboardingProgress;
    classificationRejections?: ClassificationRejection[];
    /**
     * The $25 joining bonus as the hub shows it. Null when there is nothing true
     * and positive to say (not eligible, deadline missed); absent from an older
     * API. Either way the hub shows nothing.
     */
    bonusOffer?: OnboardingBonusOffer | null;
}

export interface OnboardingBonusOffer {
    amountCents: number;
    /** YYYY-MM-DD, New York; the whole day counts. */
    deadline: string;
    daysLeft: number;
    /** "open": finish by the deadline; "done_in_time": finished, added on activation; "earned". */
    state: "open" | "done_in_time" | "earned";
}

export interface OnboardingStatusResponse {
    submissions: {
        itemId: string;
        status: string;
        rejectionReason: string | null;
        updatedAt: string | null;
    }[];
    followUps: {
        itemId: string;
        status: string;
        triggeredAt: string | null;
    }[];
    activated: boolean;
    activatedAt: string | null;
}

// --- API Functions ---

export function startOnboarding(rnId: string) {
    return request<{ onboarding: OnboardingState["onboarding"]; submissionsCreated: number }>(
        `/rn/${rnId}/onboarding/start`,
        { method: "POST", body: "{}" }
    );
}

export function fetchOnboardingState(rnId: string) {
    return request<OnboardingState>(`/rn/${rnId}/onboarding`);
}

export interface UploadHistoryEntry {
    s3Url: string;
    filename: string | null;
    fileType: string | null;
    uploadedAt: string | null;
    viewUrl: string | null;
    status: "approved" | "rejected" | "verifying" | "unrecognized" | "pending";
    items: Array<{ itemName: string; status: string; rejectionReason: string | null }>;
    classificationReason: string | null;
}

export function fetchUploadHistory(rnId: string, subsectionId?: string | number) {
    const qs = subsectionId != null ? `?subsectionId=${subsectionId}` : "";
    return request<{ uploads: UploadHistoryEntry[] }>(`/rn/${rnId}/onboarding/uploads${qs}`);
}

export function saveOnboardingAnswers(
    rnId: string,
    answers: { itemId: string; answerValue?: string; answerJson?: unknown }[]
) {
    return request<{ updated: number }>(`/rn/${rnId}/onboarding/answers`, {
        method: "POST",
        body: JSON.stringify({ answers })
    });
}

export function submitUpload(
    rnId: string,
    data: { filename: string; contentType: string; submissionId?: string }
) {
    return request<{ upload: ObUpload; uploadUrl: string }>(`/rn/${rnId}/onboarding/upload`, {
        method: "POST",
        body: JSON.stringify(data)
    });
}

export function confirmUpload(rnId: string, uploadId: string) {
    return request<{ confirmed: boolean; uploadId: string }>(
        `/rn/${rnId}/onboarding/upload/${uploadId}/confirm`,
        { method: "POST", body: "{}" }
    );
}

export function triggerLicenseValidation(rnId: string, itemId: string, licenseNumber: string) {
    return request<{ triggered: boolean; submissionId: number }>(
        `/rn/${rnId}/onboarding/trigger-license-validation`,
        {
            method: "POST",
            body: JSON.stringify({ answers: [{ itemId, answerValue: licenseNumber }] })
        }
    );
}

export function submitSignature(rnId: string, itemId: string, signatureData: string) {
    return request<ObSubmission>(`/rn/${rnId}/onboarding/signature`, {
        method: "POST",
        body: JSON.stringify({ itemId, signatureData })
    });
}

export function fetchOnboardingStatus(rnId: string) {
    return request<OnboardingStatusResponse>(`/rn/${rnId}/onboarding/status`);
}

export function fetchOnboardingProgress(rnId: string) {
    return request<OnboardingProgress>(`/rn/${rnId}/onboarding/progress`);
}

export function triggerIdentityVerification(rnId: string) {
    return request<{ triggered: boolean; selfieSubmissionId: string }>(
        `/rn/${rnId}/onboarding/verify-identity`,
        { method: "POST", body: "{}" }
    );
}

export function triggerEmailVerification(rnId: string, itemId: string) {
    return request<{ sent: boolean; email: string }>(
        `/rn/${rnId}/onboarding/trigger-email-verification`,
        { method: "POST", body: JSON.stringify({ itemId }) }
    );
}

/** Mint (or reuse) the public link for a reference block, so she can send the
 *  reference an actual URL instead of a message asking them to expect a call.
 *
 *  The path starts at /rn like every other call here: getApiBase() already ends
 *  in /api, and the extra /api this used to carry sent every tap to a route that
 *  does not exist (/api/api/..., a 404) from July 28 to October 2026. The "{}"
 *  body is required too, as on verify-identity: `request` always declares JSON,
 *  and Fastify rejects a JSON request with an empty body. */
export async function fetchReferenceLink(
    rnId: number,
    subsectionId: number
): Promise<{ url: string; referenceName: string }> {
    return request(`/rn/${rnId}/onboarding/subsections/${subsectionId}/reference-link`, {
        method: "POST",
        body: "{}"
    });
}
